import { Button, Dropdown, Input, Select, Switch } from "antd";
import { ColumnsType } from "antd/es/table";
import { capitalize } from "lodash";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../../../app/store";
import CaretDownIcon from "../../../../../assets/icons/CaretDownIcon";
import DeleteIcon from "../../../../../assets/icons/DeleteIcon";
import EditIcon from "../../../../../assets/icons/EditIcon";
import GridViewIcon from "../../../../../assets/icons/GridViewIcon";
import ListViewIcon from "../../../../../assets/icons/ListViewIcon";
import SearchIcon from "../../../../../assets/icons/SearchIcon";
import ThreeDotsIcon from "../../../../../assets/icons/ThreeDotsIcon";
import TicketsIcon from "../../../../../assets/icons/TicketsIcon";
import ViewIcon from "../../../../../assets/icons/ViewIcon";
import EmptySvg from "../../../../../assets/images/empty.svg";
import ActionModal from "../../../../../components/ui/ActionModal";
import CustomPagination from "../../../../../components/ui/CustomPagination";
import CustomTable from "../../../../../components/ui/CustomTable";
import {
  useDeleteTemplateMutation,
  useGetAllTemplatesQuery,
  useUpdateTemplateStatusMutation,
} from "../../../../../features/superAdmin/ticketService/template/template.api";
import { AllTemplateParams } from "../../../../../features/superAdmin/ticketService/template/template.type";
import useError from "../../../../../hooks/useError";
import useModal from "../../../../../hooks/useModal";
import useTable from "../../../../../hooks/useTable";
import { toast } from "../../../../../lib/toast";
import { GetAllTicketsResponseType } from "../../../../../types/tickets";
import { addKeyInArray } from "../../../../../utils/utils";
import { TEMPLATE_TYPES } from "../../constants/ticket.constants";
import TemplateCard from "./TemplateCard";



export default function TemplateTable() {
  const [layout, setLayout] = useState<"grid" | "list">(
    (localStorage.getItem("template_layout") as "grid" | "list") || "list",
  );
  const { open, handleOpen, handleClose } = useModal();
  const { role } = useAppSelector((state) => state.auth);

  const [
    deleteTemplate,
    {
      isLoading: deleting,
      isSuccess: deleted,
      isError: isDeleteError,
      error: deleteError,
    },
  ] = useDeleteTemplateMutation();

  const {
    page,
    limit,
    handleLimit,
    handlePagination,
    handleFilter,
    filter,
    keyword,
    handleSearch,
  } = useTable<AllTemplateParams>({
    defaultFilter: {
      status: "all",
      ticket_type: "",
    },
  });

  const {
    isLoading,
    isFetching,
    isError,
    error,
    data: ticketData,
  } = useGetAllTemplatesQuery({
    params: {
      ...filter,
      skip: undefined,
      search: keyword,
      status: filter.status === "all" ? undefined : filter.status,
      page,
      ticket_type: filter.ticket_type,
      limit,
    },
  });
  const templates = ticketData?.data?.tickets;

  const [
    updateTemplateStatus,
    {
      isError: isUpdateStatusError,
      error: updateStatusError,
      isSuccess: isUpdatedStatus,
    },
  ] = useUpdateTemplateStatusMutation();

  const handleDeleteTemplate = () => {
    deleteTemplate({ id: (open?.data as unknown as { id: string })?.id });
  };

  const handleChangeTemplateStatus = (id: any, currentStatus: string) => {
    const newStatus = currentStatus === "ACTIVE" ? "inactive" : "active";
    const data = {
      status: newStatus,
    };
    updateTemplateStatus({ id, data });
  };

  useEffect(() => {
    if (isUpdatedStatus) {
      toast("success", "Template status updated successfully!");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdatedStatus]);

  useEffect(() => {
    if (deleted) {
      toast("success", "Template deleted successfully!");
    }
    if (!deleting) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted, deleting]);

  useEffect(() => {
    localStorage.setItem("template_layout", layout);
  }, [layout]);

  // Errors
  useError(isDeleteError, deleteError, "Template delete fail");
  useError(
    isUpdateStatusError,
    updateStatusError,
    "Template status update fail",
  );
  useError(isError, error, "Get all templates list fail");

  const columns: ColumnsType<GetAllTicketsResponseType> = [
    {
      title: "Template name",
      width: 200,
      dataIndex: "name",
      render: (val) => {
        return <span className="block min-w-[100px]">{val}</span>;
      },
    },
    {
      title: "Template Key",
      align: "center",
      render: (val) => (
        <Link
          className="text-nowrap"
          to={`/admin/ticket-management/all-templates/template-details/${val.id}`}
        >
          {val?.template_key ?? "N/A"}
        </Link>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      align: "center",
      render: (val) => (
        <span className="status status-primary">{capitalize(val)}</span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      align: "center",
      render: (val) => (
        <span
          className={`status ${
            val === TEMPLATE_TYPES.sla ? "status-primary" : "status-info"
          }`}
        >
          {val === TEMPLATE_TYPES.sla ? "SLA Based" : "Workflow Based"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      render: (val) => (
        <span
          className={`status ${
            val === "ACTIVE" ? "status-success" : "status-danger"
          }`}
        >
          {capitalize(val)}
        </span>
      ),
    },
    {
      title: "Action",
      align: "center",
      render: (val) => {
        return (
          <div className="flex items-center justify-center">
            <Dropdown
              menu={{
                items: [
                  {
                    label: (
                      <div className="flex justify-between items-center">
                        <span>Active</span>
                        <span>
                          <Switch
                            checked={val?.status === "ACTIVE"}
                            onChange={() =>
                              handleChangeTemplateStatus(val?.id, val?.status)
                            }
                          />
                        </span>
                      </div>
                    ),
                    key: "0",
                  },
                  {
                    type: "divider",
                  },
                  {
                    label: (
                      <Link
                        to={`/admin/ticket-management/all-templates/template-details/${val.id}`}
                      >
                        <div className="flex gap-2 items-center">
                          <span>
                            <ViewIcon />
                          </span>
                          <span>View Details</span>
                        </div>
                      </Link>
                    ),
                    key: "1",
                  },

                  ...(role === "super_admin" || role === "Admin"
                    ? [
                        {
                          label: (
                            <Link
                              to={`/admin/ticket-management/all-templates/edit-ticket/${val.id}`}
                            >
                              <div className="flex gap-2 items-center">
                                <span>
                                  <EditIcon />
                                </span>
                                <span>Edit Template</span>
                              </div>
                            </Link>
                          ),
                          key: "2",
                        },
                        {
                          type: "divider" as const,
                        },
                        {
                          label: (
                            <div
                              onClick={() =>
                                handleOpen({ type: "VIEW", data: val })
                              }
                              className="flex gap-2 items-center"
                            >
                              <span>
                                <DeleteIcon />
                              </span>
                              <span>Delete </span>
                            </div>
                          ),
                          key: "3",
                        },
                      ]
                    : []),
                ],
              }}
              trigger={["click"]}
            >
              <div className="cursor-pointer">
                <ThreeDotsIcon />
              </div>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {open.type === "VIEW" && (
        <ActionModal
          open={open?.open}
          loading={deleting}
          title={"Do you want to delete this template?"}
          subTitle={
            "Are you sure you want to delete this template? This action cannot be."
          }
          iconType="danger"
          actionBtnText={`Yes, Delete`}
          actionBtnIcon
          actionBtnIconType="delete"
          actionBtnType="danger-light"
          cancelButton={false}
          handleClose={handleClose}
          handleAction={handleDeleteTemplate}
          handleCancel={handleClose}
        />
      )}
      <div className="flex justify-between items-center flex-wrap">
        <div className="flex gap-1 items-center">
          <div>Total Rows</div>
          <Select
            placeholder="10"
            suffixIcon={<CaretDownIcon />}
            size="middle"
            style={{ width: 70 }}
            onChange={handleLimit}
            options={[
              { label: "10", value: 10 },
              { label: "20", value: 20 },
              { label: "50", value: 50 },
              { label: "100", value: 100 },
              { label: "1000", value: 1000 },
            ]}
          />
          <div>of {ticketData?.data?.totalLength || 0}</div>
        </div>

        <div className="flex gap-2 items-center flex-wrap">
          <Input
            placeholder="Search by Name, Id"
            suffix={<SearchIcon />}
            size="middle"
            className="max-w-[200px]"
            onChange={handleSearch}
          />
          <Select
            placeholder="Category"
            className="min-w-28"
            suffixIcon={<CaretDownIcon />}
            size="middle"
            onChange={(value) => handleFilter("category", value)}
            options={[
              {
                label: "General",
                value: "general",
              },
              {
                label: "Product",
                value: "product",
              },
            ]}
            allowClear
          />
          <Select
            placeholder="Type"
            suffixIcon={<CaretDownIcon />}
            size="middle"
            style={{ width: 150 }}
            onChange={(value) => handleFilter("ticket_type", value)}
            options={[
              { label: "All", value: "" },
              { label: "SLA Based", value: TEMPLATE_TYPES.sla },
              { label: "Workflow Based", value: TEMPLATE_TYPES.workflow },
            ]}
            allowClear
          />
          <Select
            allowClear
            size="middle"
            className="min-w-28"
            placeholder="Status"
            style={{ width: 85 }}
            suffixIcon={<CaretDownIcon />}
            onChange={(value) => handleFilter("status", value)}
            options={[
              { label: "All", value: "all" },
              { label: "Active", value: "active" },
              { label: "Inactive", value: "inactive" },
            ]}
          />
          {role === "super_admin" || role === "Admin" ? (
            <Link to="/admin/ticket-management/all-templates/create-new">
              <Button className="button-primary-light h-9">
                <TicketsIcon />
                Create Template
              </Button>
            </Link>
          ) : null}

          <div
            className={`cursor-pointer ${
              layout === "grid" ? "text-[#FE7235]" : ""
            } text-[18px]`}
            onClick={() => setLayout("grid")}
          >
            <GridViewIcon />
          </div>
          <div
            className={`cursor-pointer ${
              layout === "list" ? "text-[#FE7235]" : ""
            } text-[18px]`}
            onClick={() => setLayout("list")}
          >
            <ListViewIcon />
          </div>
        </div>
      </div>

      {layout === "grid" && (
        <>
          {templates?.length !== 0 ? (
            <>
              <div className="grid mt-4 xl:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4">
                {templates?.map((ticket) => (
                  <TemplateCard key={ticket.id} template={ticket} />
                ))}
              </div>

              <CustomPagination
                total={ticketData?.data?.totalLength || 0}
                limit={limit}
                page={page}
                handlePagination={handlePagination}
              />
            </>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "50vh",
                }}
              >
                <img src={EmptySvg} alt="empty_image" />
              </div>
            </>
          )}
        </>
      )}

      {layout === "list" && (
        <div className="mt-4">
          <CustomTable
            loading={isLoading || isFetching}
            limit={limit}
            page={page}
            columns={columns}
            handlePagination={handlePagination}
            total={ticketData?.data?.totalLength || 0}
            dataSource={addKeyInArray(templates)}
          />
        </div>
      )}
    </div>
  );
}
